import React from "react";
import { gql } from "@apollo/client";
import { batch } from "react-redux";
import { Link } from "@react-navigation/native";
import { captureException } from "@sentry/browser";
import { AppState } from "@/reducers";
import { authenticate, getUserFromToken, setTokenToCookie } from "./auth";
import { apollo, AppDispatch, AppThunk, formHandleInput } from "./index";
import {
  ONBOARDING_ERROR_SET,
  ONBOARDING_ERROR_RESET,
  ONBOARDING_BADGETOKEN_ERROR,
  ONBOARDING_ENTITYCODE_ERROR,
  ONBOARDING_LOADING_END,
  ONBOARDING_LOADING_START,
  ONBOARDING_STEP_CUSTOM,
  ONBOARDING_STEP_NEXT,
  ONBOARDING_STEP_PREVIOUS,
  AUTH_LOGIN_ERROR,
  AUTH_SIGN_UP_EMAIL_ERROR,
} from "./types";
import * as signupNavigation from "../navigators/Signup/SignupNavigatorRef";
import SegmentClient from "../helpers/SegmentClient";
import { some } from "lodash";
import type { TranslateOptions } from "@/i18n";
import { translateMessage } from "@/helpers/translation";

const translateOnboardingMessage = (key: string, options?: TranslateOptions) =>
  translateMessage(`onboarding.messages.${key}`, options);

const getOnboardingMessages = () => ({
  emailAlreadyLinked: translateOnboardingMessage("emailAlreadyLinked", {
    defaultValue: "Cet email est déjà relié à un compte.",
  }),
  accountExists: translateOnboardingMessage("accountExists", {
    defaultValue: "Un compte existe déjà pour cette adresse email",
  }),
  onboardingNotAvailable: translateOnboardingMessage("onboardingNotAvailable", {
    defaultValue:
      "Minute papillon, encore quelques jours de patience avant l'ouverture des inscriptions 🙂",
  }),
  unknownBadgeToken: translateOnboardingMessage("unknownBadgeToken", {
    defaultValue: "Le code entré semble incorrect, veuillez vérifier votre saisie puis réessayez.",
  }),
  technicalError: translateOnboardingMessage("technicalError", {
    defaultValue: "Une erreur technique est survenue, veuillez réessayer.",
  }),
  checkoutSystemDenied: translateOnboardingMessage("checkoutSystemDenied", {
    defaultValue:
      "La création de votre compte a été refusée par le système de caisse. Veuillez vous rapprocher du chef de secteur Popchef ou réessayez ultérieurement.",
  }),
  loginPrompt: translateOnboardingMessage("loginPrompt", {
    defaultValue: "Souhaitez-vous vous connecter ?",
  }),
});

export const goToNextOnboardingStep = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: ONBOARDING_STEP_NEXT });
};

export const goToPreviousOnboardingStep = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: ONBOARDING_STEP_PREVIOUS });
};

export const goToCustomOnboardingStep =
  (step: number): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: ONBOARDING_STEP_CUSTOM, step });
  };

export const resetOnboardingError = (): AppThunk => async (dispatch: AppDispatch) => {
  dispatch({ type: ONBOARDING_ERROR_RESET });
};

export const checkCustomerByEmail =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getOnboardingMessages();
    dispatch({ type: ONBOARDING_LOADING_START });

    const {
      form: {
        signup: { email },
      },
    } = getState();

    try {
      const req = await apollo().query<any>({
        variables: { email },
        query: gql`
          query CheckCustomerExistByEmail($email: String!) {
            checkCustomerExistByEmail(email: $email)
          }
        `,
      });
      if (req && req?.data?.checkCustomerExistByEmail) {
        // set message error
        dispatch({
          type: AUTH_SIGN_UP_EMAIL_ERROR,
          message: messages.emailAlreadyLinked,
        });
      } else {
        signupNavigation.navigate("signupStepName");
      }
    } catch (err) {
      captureException(err);
    } finally {
      batch(() => {
        dispatch({ type: ONBOARDING_LOADING_END });
        dispatch(goToNextOnboardingStep());
      });
    }
  };

export const findPendingCustomerByEmail =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: ONBOARDING_LOADING_START });

    const {
      form: {
        signup: { email },
      },
    } = getState();

    try {
      const req = await apollo().query<any>({
        variables: { email },
        query: gql`
          query ($email: String!) {
            pendingCustomerByEmail(email: $email) {
              firstname
              lastname
            }
          }
        `,
      });

      if (req && req.data && req.data.pendingCustomerByEmail) {
        batch(() => {
          dispatch(
            formHandleInput("signup", "firstname", req.data.pendingCustomerByEmail.firstname),
          );
          dispatch(formHandleInput("signup", "lastname", req.data.pendingCustomerByEmail.lastname));
        });
      }
    } catch (err) {
      captureException(err);
    } finally {
      batch(() => {
        dispatch({ type: ONBOARDING_LOADING_END });
        dispatch(goToNextOnboardingStep());
      });
    }
  };

export const submitOnboardingByPendingCustomer =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getOnboardingMessages();
    batch(() => {
      dispatch({ type: ONBOARDING_LOADING_START });
      dispatch({ type: ONBOARDING_ERROR_RESET });
    });

    const {
      form: {
        signup: { email, firstname, lastname, hasAcceptedNewsletter, password },
      },
    } = getState();

    try {
      const req = await apollo().mutate<any>({
        variables: {
          email,
          firstname,
          lastname,
          hasAcceptedNewsletter,
          password,
        },
        mutation: gql`
          mutation (
            $email: String!
            $firstname: String!
            $lastname: String!
            $hasAcceptedNewsletter: Boolean!
            $password: String!
          ) {
            submitOnboardingByPendingCustomer(
              email: $email
              firstname: $firstname
              lastname: $lastname
              hasAcceptedNewsletter: $hasAcceptedNewsletter
              password: $password
            )
          }
        `,
      });
      if (req && req.data && req.data.submitOnboardingByPendingCustomer) {
        batch(() => {
          dispatch({ type: ONBOARDING_LOADING_END });

          switch (req.data.submitOnboardingByPendingCustomer) {
            case "MatchingPendingCustomer":
              signupNavigation.navigate("signupStepEmailCheck");
              break;
            case "CustomerAlreadyExists":
              SegmentClient.track("Sign Up Failed", {
                message: messages.accountExists,
                data: { email },
              });
              dispatch({
                type: ONBOARDING_ERROR_SET,
                message: (
                  <>
                    {messages.accountExists}
                    <Link to="loginModal">{messages.loginPrompt}</Link>
                  </>
                ),
              });

              break;
            case "OnboardingNotAvailable":
              SegmentClient.track("Sign Up Failed\n", {
                message: messages.onboardingNotAvailable,
                data: { email },
              });
              dispatch({
                type: ONBOARDING_ERROR_SET,
                message: messages.onboardingNotAvailable,
              });

              break;
            case "UnknownPendingCustomer":
              signupNavigation.navigate("signupStepUnknownEmail");

              break;
          }
        });
      }
    } catch (err) {
      captureException(err);
    }
  };

export const checkPendingCustomerSignup =
  (timer: NodeJS.Timer | null, redirect?: string): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const {
      form: {
        signup: { email, password },
      },
    } = getState();

    try {
      const req = await apollo().mutate<any>({
        variables: { email, password },
        mutation: gql`
          mutation ($email: String!, $password: String!) {
            checkPendingCustomerSignup(email: $email, password: $password) {
              token
            }
          }
        `,
      });
      if (req?.data?.checkPendingCustomerSignup?.token) {
        if (timer) {
          clearInterval(timer);
        }
        setTokenToCookie(req?.data?.checkPendingCustomerSignup?.token).then(() => {
          dispatch(getUserFromToken(req?.data?.checkPendingCustomerSignup?.token, redirect));
        });
      }
    } catch (err) {
      captureException(err);
    }
  };

export const submitOnboardingByBadge =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getOnboardingMessages();
    batch(() => {
      dispatch({ type: ONBOARDING_LOADING_START });
    });

    const form = getState().form.signup;
    const badgeToken =
      String(form.badgeToken1) +
      String(form.badgeToken2) +
      String(form.badgeToken3) +
      String(form.badgeToken4);
    const {
      form: {
        signup: { email, firstname, lastname, hasAcceptedNewsletter, password },
      },
    } = getState();

    try {
      const req = await apollo().mutate<any>({
        variables: {
          email,
          firstname,
          lastname,
          hasAcceptedNewsletter,
          password,
          badgeToken,
        },
        mutation: gql`
          mutation (
            $email: String!
            $firstname: String!
            $lastname: String!
            $hasAcceptedNewsletter: Boolean!
            $password: String!
            $badgeToken: String!
          ) {
            submitOnboardingByBadge(
              email: $email
              firstname: $firstname
              lastname: $lastname
              hasAcceptedNewsletter: $hasAcceptedNewsletter
              password: $password
              badgeToken: $badgeToken
            )
          }
        `,
      });

      if (req && req.data && req.data.submitOnboardingByBadge) {
        switch (req.data.submitOnboardingByBadge) {
          case "CustomerAccountCreated":
            batch(() => {
              dispatch(formHandleInput("login", "email", email));
              dispatch(formHandleInput("login", "password", password));
            });

            dispatch(authenticate({ redirect: "onboardingRefillModal" }));
            dispatch({ type: ONBOARDING_LOADING_END });

            break;
          case "CustomerAlreadyExists":
            batch(() => {
              dispatch({
                type: ONBOARDING_BADGETOKEN_ERROR,
                message: messages.accountExists,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
          case "UnknownBadgeToken":
            batch(() => {
              dispatch({
                type: ONBOARDING_BADGETOKEN_ERROR,
                message: messages.unknownBadgeToken,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;

          case "OnboardingNotAvailable":
            batch(() => {
              dispatch({
                type: ONBOARDING_BADGETOKEN_ERROR,
                message: (
                  <>
                    Minute papillon, encore quelques jours de patience avant l'ouverture des
                    inscriptions 🙂
                  </>
                ),
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
          case "TechnicalError":
            batch(() => {
              dispatch({
                type: ONBOARDING_BADGETOKEN_ERROR,
                message: messages.technicalError,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
          case "CheckoutSystemDeniedAccountCreation":
            batch(() => {
              dispatch({
                type: ONBOARDING_BADGETOKEN_ERROR,
                message: messages.checkoutSystemDenied,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
        }
      } else if (req?.errors?.length > 0) {
        if (some(req?.errors, error => error?.message === "UnauthorizedEmailPattern")) {
          const unauthorizedEmailPatternError = (req?.errors as any)?.find(
            (error: any) => error?.message === "UnauthorizedEmailPattern",
          );
          const allowedPatterns = (unauthorizedEmailPatternError as any)?.data?.allowedPatterns;
          signupNavigation.navigate("signupStepEmail", { allowedPatterns });
        }
      }
    } catch (err) {
      captureException(err);
    }
  };

export const submitOnboardingByEntityCode =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getOnboardingMessages();
    batch(() => {
      dispatch({ type: ONBOARDING_LOADING_START });
    });

    const form = getState().form.signup;
    const codeToken =
      String(form.codeToken1) +
      String(form.codeToken2) +
      String(form.codeToken3) +
      String(form.codeToken4) +
      String(form.codeToken5);

    const {
      form: {
        signup: { email, firstname, lastname, hasAcceptedNewsletter, password },
      },
    } = getState();

    try {
      const req = await apollo().mutate<any>({
        variables: {
          email,
          firstname,
          lastname,
          hasAcceptedNewsletter,
          password,
          collectiveCateringRegistrationCode: codeToken,
        },
        mutation: gql`
          mutation (
            $email: String!
            $firstname: String!
            $lastname: String!
            $hasAcceptedNewsletter: Boolean!
            $password: String!
            $collectiveCateringRegistrationCode: String!
          ) {
            submitOnboardingByCollectiveCateringRegistrationCode(
              email: $email
              firstname: $firstname
              lastname: $lastname
              hasAcceptedNewsletter: $hasAcceptedNewsletter
              password: $password
              collectiveCateringRegistrationCode: $collectiveCateringRegistrationCode
            )
          }
        `,
      });

      if (req && req.data && req.data.submitOnboardingByCollectiveCateringRegistrationCode) {
        switch (req.data.submitOnboardingByCollectiveCateringRegistrationCode) {
          case "CustomerAccountCreated":
            batch(() => {
              dispatch(formHandleInput("login", "email", email));
              dispatch(formHandleInput("login", "password", password));
            });

            dispatch(authenticate({ redirect: "onboardingRefillModal" }));
            dispatch({ type: ONBOARDING_LOADING_END });

            break;
          case "CustomerAlreadyExists":
            batch(() => {
              dispatch({
                type: ONBOARDING_ENTITYCODE_ERROR,
                message: messages.accountExists,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
          case "UnknownEntityCode":
            batch(() => {
              dispatch({
                type: ONBOARDING_ENTITYCODE_ERROR,
                message: messages.unknownBadgeToken,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
          case "OnboardingNotAvailable":
            batch(() => {
              dispatch({
                type: ONBOARDING_ENTITYCODE_ERROR,
                message: messages.onboardingNotAvailable,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });

            break;
          case "TechnicalError":
            batch(() => {
              dispatch({
                type: ONBOARDING_ENTITYCODE_ERROR,
                message: messages.technicalError,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });
            break;
          case "CheckoutSystemDeniedAccountCreation":
            batch(() => {
              dispatch({
                type: ONBOARDING_ENTITYCODE_ERROR,
                message: messages.checkoutSystemDenied,
              });
              dispatch({ type: ONBOARDING_LOADING_END });
            });
            break;
        }
      } else if (req?.errors?.length > 0) {
        if (some(req?.errors, error => error?.message === "UnauthorizedEmailPattern")) {
          const unauthorizedEmailPatternError = (req?.errors as any)?.find(
            (error: any) => error?.message === "UnauthorizedEmailPattern",
          );
          const allowedPatterns = (unauthorizedEmailPatternError as any)?.data?.allowedPatterns;
          signupNavigation.navigate("signupStepEmail", { allowedPatterns });
        }
      }
    } catch (err) {
      captureException(err);
    }
  };
